import React, { useEffect } from 'react'
import { Image, ImageStyle, StatusBar, View, ViewStyle } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { CommonActions, useNavigation } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Text } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { steloUpsellImage } from '@src/assets/images'
import { InlineAlert, LinearGradient } from '@src/components'
import {
  BillingProduct,
  LegalPolicyConsentKind,
  LegalPolicyConsentState,
  SurveysConfigKind,
  UiInlineAlert,
  UserPropertyKind,
} from '@src/types'
import { productsSelector } from '@src/selectors/app'
import { Storage } from '@src/utils'
import { Analytics, CustomEventTypes } from '@src/config'
import { policyConsentStatusesSelector } from '@src/selectors/policyConsents'
import { LoadingContainer } from '../Common/containers/LoadingContainer'

const OLD_PRICE = '$199'
const STATUS_BAR_BACKGROUND_COLOR = '#f1d993'
const BACKGROUND_COLOR = '#102F32'
const OLD_PRICE_TEXT_COLOR = '#FFBB36'
const ALERT_BACKGROUND_COLOR = '#30343780'
const INFO_TEXT_COLOR = '#B5B5B6'
const DISMISS_TEXT_COLOR = '#FFFFFF'

const PRODUCT_TITLE = '2 x 15 Day Stelo Glucose Biosensors'

export const SteloUpsellScreen = () => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation()
  const insets = useSafeAreaInsets()
  const dispatch = useDispatch()

  const products = useSelector(productsSelector)

  const product = products.find((product) => product.key === BillingProduct.SteloCgm_2)

  const policyConsentStatuses = useSelector(policyConsentStatusesSelector)

  useEffect(() => {
    dispatch({
      type: 'app/fetchProducts',
    })
  }, [dispatch])

  useEffect(() => {
    Storage.set(Storage.VIDEO_CALLS_STELO_UPSELL_MODAL_VISITED_KEY, true)
  }, [])

  const handlePurchase = () => {
    if (!product) {
      return
    }

    Analytics.track(CustomEventTypes.VideoCallsSteloUpsellScreenPurchaseNow)

    navigation.dispatch((state) => {
      // Remove current route
      const routes = state.routes.slice(0, -1)

      const medicalDisclaimerConsentAccepted = policyConsentStatuses.some(
        (status) =>
          status.kind === LegalPolicyConsentKind.MedicalDisclaimer &&
          status.lastPolicyConsent?.state === LegalPolicyConsentState.Accepted,
      )

      const nextRoute = medicalDisclaimerConsentAccepted
        ? {
            name: 'SteloCheckout',
            params: { product: { ...product, title: PRODUCT_TITLE } },
          }
        : {
            name: 'Questionnaire',
            params: {
              allowEarlyExit: true,
              questionnaire: SurveysConfigKind.CgmMedicalDisclaimer,
              nextScreen: {
                screen: 'SteloCheckout',
                params: { product: { ...product, title: PRODUCT_TITLE } },
              },
            },
          }

      const newRoutes = [...routes, nextRoute]

      return CommonActions.reset({
        index: newRoutes.length - 1,
        routes: newRoutes,
      } as any)
    })
  }

  const handleDismiss = () => {
    Analytics.track(CustomEventTypes.VideoCallsSteloUpsellScreenDismiss)

    dispatch({
      type: 'users/upsertUserProperty',
      payload: {
        kind: UserPropertyKind.VideoCallSteloUpsellModalDismissed,
        value: 'true',
      },
    })

    navigation.goBack()
  }

  if (!product) {
    return <LoadingContainer />
  }

  return (
    <View style={{ flex: 1, backgroundColor: STATUS_BAR_BACKGROUND_COLOR }}>
      <StatusBar barStyle="dark-content" />
      <View style={{ flex: 1, backgroundColor: BACKGROUND_COLOR, marginTop: insets.top }}>
        <View style={{ position: 'relative' }}>
          <Image
            resizeMethod="resize"
            source={steloUpsellImage.imageSource}
            style={styles.image as ImageStyle}
          />
          <LinearGradient
            start={{ x: 0, y: 1 }}
            end={{ x: 0, y: 0 }}
            startColor={BACKGROUND_COLOR}
            endColor={BACKGROUND_COLOR}
            startOpacity={1}
            endOpacity={0}
            style={styles.imageContainerInner}
          />
        </View>
        <View style={styles.content}>
          <Text style={styles.title} type="title-1">
            Unlock Faster Results with a Biosensor.
          </Text>
          <View style={styles.priceContainer}>
            <Text style={[styles.oldPrice, { color: OLD_PRICE_TEXT_COLOR }]} type="regular">
              {OLD_PRICE}
            </Text>
            <Text style={styles.price} type="regular">
              $160
            </Text>
            <Text style={styles.priceSubtext} type="regular">
              for 2 x 15 days Stelo Biosensor
            </Text>
          </View>
          <Text style={styles.description} type="regular">
            Get real-time insights into how your body responds to food. Pair your journey with data
            to make smarter choices and reach your goals faster.
          </Text>
          <InlineAlert
            category={UiInlineAlert.Neutral}
            style={[
              styles.info,
              {
                backgroundColor: ALERT_BACKGROUND_COLOR,
                borderColor: ALERT_BACKGROUND_COLOR,
              },
            ]}
            textStyle={{ color: INFO_TEXT_COLOR } as ViewStyle}
            iconStyle={{ color: INFO_TEXT_COLOR } as ViewStyle}
            message="Start with one month, no commitment, renew when you want!"
          />
        </View>
        <View style={styles.buttonContainer}>
          <Button
            type="primary"
            size="block"
            onPress={handlePurchase}
            accessibilityLabel="Purchase now"
          >
            Purchase now
          </Button>
          <Button
            type="transparent"
            size="block"
            onPress={handleDismiss}
            accessibilityLabel="Dismiss"
            textStyle={{ color: DISMISS_TEXT_COLOR }}
          >
            Dismiss
          </Button>
        </View>
      </View>
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.text.primary',
  },
  image: {
    maxWidth: 440,
    height: undefined,
    maxHeight: 300,
    alignSelf: 'center',
    aspectRatio: 16 / 9,
  },
  imageContainerInner: {
    height: 52,
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '100%',
  },
  content: {
    flex: 1,
    padding: 24,
    textAlign: 'left',
  },
  title: {
    fontSize: 30,
    fontWeight: '400',
    lineHeight: 32,
    maxWidth: 300,
    marginBottom: 16,
    color: 'theme.solid.white',
  },
  priceContainer: {
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    color: 'theme.solid.white',
  },
  oldPrice: {
    textDecorationLine: 'line-through',
    fontWeight: '700',
    color: 'theme.solid.white',
  },
  price: {
    fontWeight: '700',
    color: 'theme.solid.white',
  },
  priceSubtext: {
    fontWeight: '700',
    color: 'theme.solid.white',
  },
  description: {
    marginBottom: 24,
    fontWeight: '500',
    color: 'theme.solid.white',
  },
  info: {
    marginBottom: 24,
  },
  buttonContainer: {
    gap: 12,
    paddingHorizontal: 24,
    paddingBottom: 40,
  },
})
